import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FooterComponent, HeaderComponent } from 'component-library';
import { ProductData } from 'europace-components';
import { OrderStatus } from 'frontend.client.api';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BirthdayDialogComponent } from './components/shared/birthday-dialog/birthday-dialog.component';
import { OrderService } from './services/order/order.service';
import { PartnerStateService } from './shared/partner-state.service';
import { RouterService } from './shared/router.service';
import { ThemeService } from './shared/theme.service';
import { ThemeTestService } from './shared/theme-test.service';
import { BackwardCompatibilityTestService } from './shared/backward-compatibility-test.service';
import { environment } from '../environments/environment';
import { RoutePaths } from './app.routes';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, FooterComponent, HeaderComponent, CommonModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  protected epIconSrcPath = 'assets/europaceLogo.svg';
  protected valIconSrcPath = 'assets/value_ag.png';
  private orderLockStateSubscription: Subscription | undefined;
  private partnerSubscription: Subscription | undefined;
  private orderStateSubscription: Subscription | undefined;
  private themeSubscription: Subscription | undefined;
  private dialog = inject(MatDialog);

  public productData: ProductData[] | undefined = [];
  public partnerLogoUrl = signal('');
  public partnerLoaded = signal(false);
  public themeLoaded = signal(false);
  public themeError = signal<string | undefined>(undefined);
  public themeFallbackActive = signal(false);
  public productCount = signal<number | undefined>(undefined);
  public impressUrl = 'https://europace.de/impressum/';
  public dataProtectionUrl = '/datenschutz';
  public termsOfUseUrl = '/agb';
  public isStaticRoute = computed( () => {
    const path= this.routerService.routerState$.getValue().path ?? '';
    return this.routerService.staticRoutes.includes(path);
    }
  )
  public showHeader = true;
  public isHomePage = signal(false);
  private readonly homePath: RoutePaths = 'unterlagenauswahl';

  constructor(
    private orderService: OrderService,
    private themeService: ThemeService,
    private themeTestService: ThemeTestService,
    private backwardCompatibilityTestService: BackwardCompatibilityTestService,
    private partnerState: PartnerStateService,
    private router: Router,
    private routerService: RouterService,
  ) {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe((event) => {
      const path = (event as NavigationEnd).url;
      if(this.routerService.staticRoutes.includes(path))
        this.showHeader = false;
      else
        this.showHeader = true;
      this.isHomePage.set(
        (event as NavigationEnd).urlAfterRedirects
          .split(/[?#]/)[0]
          .endsWith(`/${this.homePath}`),
      );
    });
  }



  public handleLinkClicked(event: any): void {
    const path = event.detail.toLowerCase();
    if(this.routerService.staticRoutes.includes(path)) {
      this.routerService.router.navigate([path], {});
      return;
    }
    this.routerService.routeToInCurrentSubfolder(path);
  }

  public ngOnInit(): void {
    console.log('b2c-unterlagenshop: 0.1.2-cache-fix');

    this.getHeapIdFromEnv();

    // Subscribe to theme state changes
    this.themeSubscription = this.themeService.themeState$.subscribe(
      (themeState) => {
        this.themeLoaded.set(!!themeState.currentTheme && !themeState.isLoading);
        this.themeError.set(themeState.error);
        this.themeFallbackActive.set(themeState.fallbackActive);

        // Log theme status for debugging
        if (!environment.production) {
          console.log('Theme state updated:', {
            currentTheme: themeState.currentTheme,
            partnerTheme: themeState.partnerTheme?.cssFileName,
            fallbackActive: themeState.fallbackActive,
            isLoading: themeState.isLoading,
            error: themeState.error
          });

          // Run Stencil component theme test after theme changes
          setTimeout(() => this.testStencilComponentIntegration(), 1000);
        }
      },
    );

    this.orderLockStateSubscription =
      this.orderService.orderLockState$.subscribe((lock) => {
        if (lock) {
          this.dialog.open(BirthdayDialogComponent, {
            maxWidth: '660px',
            disableClose: true,
          });
        }
      });

    // Subscribe to partner state changes (theme loading is now automatic)
    this.partnerSubscription = this.partnerState.partnerState.subscribe(
      (partner) => {
        if (partner.partnerData) {
          this.partnerLogoUrl.set(partner.partnerData.partnerLogoUrl ?? '');
          this.partnerLoaded.set(true);
        } else {
          this.partnerLoaded.set(false);
          this.partnerLogoUrl.set('');
        }
      },
    );

    this.orderStateSubscription = this.orderService.orderState$.subscribe(
      (orderState) => {
        const finishedOrder: OrderStatus[] = [
          OrderStatus.PAYMENT_SUCCESS,
          OrderStatus.DELIVERED,
          OrderStatus.COMMISSIONED,
        ];
        const order = orderState.order;
        this.productData = order?.items.map((item) => ({
          name: item.productData?.name ?? '',
          price: item.productData?.price ?? 0,
        }));
        if (order && finishedOrder.includes(order?.orderStatus)) {
          this.productCount.set(undefined);
        } else {
          this.productCount.set(order?.items.length ?? undefined);
        }
      },
    );

    // Run backward compatibility test on startup (if enabled in environment)
    if (!environment.production && environment.runBackwardCompatibilityTests) {
      setTimeout(() => this.runBackwardCompatibilityTest(), 2000);
    }
  }

  /**
   * Test Stencil component integration (development only)
   */
  private async testStencilComponentIntegration(): Promise<void> {
    if (environment.production) return;

    try {
      const testResult = await this.themeTestService.testStencilComponentTheming();
      console.log(`🧪 Stencil Component Theme Test: ${testResult ? 'PASSED' : 'FAILED'}`);

      if (!testResult) {
        console.warn('Theme integration issues detected. Run themeTestService.generateThemeTestReport() for details.');
      }
    } catch (error) {
      console.error('Failed to run Stencil component theme test:', error);
    }
  }

  /**
   * Run backward compatibility test (development only)
   */
  private async runBackwardCompatibilityTest(): Promise<void> {
    if (environment.production) return;

    try {
      const results = await this.backwardCompatibilityTestService.testBackwardCompatibility();
      console.log(`🔄 Backward Compatibility Test: ${results.overall ? 'PASSED' : 'FAILED'}`);

      if (!results.overall) {
        console.warn('Backward compatibility issues detected. Check individual test results above.');
      }
    } catch (error) {
      console.error('Failed to run backward compatibility test:', error);
    }
  }

  /**
   * Manually reload theme (for development/debugging)
   */
  public async reloadTheme(): Promise<void> {
    await this.themeService.reloadTheme();
  }

  /**
   * Clear theme cache (for development/debugging)
   */
  public clearThemeCache(): void {
    this.themeService.clearCache();
  }

  /**
   * Generate and log theme test report (for development/debugging)
   */
  public async logThemeTestReport(): Promise<void> {
    if (environment.production) return;

    try {
      const report = await this.themeTestService.generateThemeTestReport();
      console.log(report);
    } catch (error) {
      console.error('Failed to generate theme test report:', error);
    }
  }

  /**
   * Generate and log backward compatibility report (for development/debugging)
   */
  public async logBackwardCompatibilityReport(): Promise<void> {
    if (environment.production) return;

    try {
      const report = await this.backwardCompatibilityTestService.generateBackwardCompatibilityReport();
      console.log(report);
    } catch (error) {
      console.error('Failed to generate backward compatibility report:', error);
    }
  }

  private getHeapIdFromEnv(): void {
    if(environment.STAGE === 'local') return;
    const headScript = document.querySelector('script[data-usercentrics="Heap"]');
    if(headScript && headScript.textContent){
      headScript.textContent = headScript.textContent.replace('{HEAP_ID}', environment.HEAP_ID);
    }
    const hs = document.querySelector('script[data-usercentrics="Heap"]');
  }

  public ngOnDestroy(): void {
    this.orderLockStateSubscription?.unsubscribe();
    this.partnerSubscription?.unsubscribe();
    this.orderStateSubscription?.unsubscribe();
    this.themeSubscription?.unsubscribe();
  }
}
